import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { moneyShort as money } from "@/lib/format";

export const ConsignmentReport = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () =>
    api.get("/inventory/consignment-report").then(({ data }) => setData(data));

  useEffect(() => {
    load();
  }, []);

  const setPaid = async (slab, paid) => {
    await api.patch(`/slabs/${slab.id}/payout`, { paid });
    toast.success(
      paid ? `${slab.code} marked paid` : `${slab.code} back to payout due`,
    );
    load();
  };

  if (!data)
    return <div className="mt-10 h-32 animate-pulse rounded-lg bg-zinc-900" />;
  const { rows, totals } = data;

  return (
    <section className="mt-12" data-testid="consignment-report">
      <div className="flex items-center gap-2">
        <Handshake size={18} weight="bold" className="text-amber-400" />
        <h2 className="text-xl font-bold">Consignment Stock</h2>
      </div>
      <p className="mt-1 text-sm text-zinc-500">
        Supplier-owned slabs you are holding — value shown where a cost per sq
        ft is on file
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          ["Suppliers", totals.suppliers, "text-white"],
          ["Slabs Held", totals.slab_count, "text-amber-400"],
          ["Sq Ft", totals.sqft, "text-emerald-400"],
          ["Value", money(totals.value), "text-yellow-400"],
          ["Payout Due", money(totals.owed_value), "text-red-400"],
        ].map(([k, v, c]) => (
          <div
            key={k}
            data-testid={`consignment-stat-${k.toLowerCase().replace(/ /g, "-")}`}
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              {k}
            </div>
            <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
              {v}
            </div>
          </div>
        ))}
      </div>

      {totals.unpriced > 0 && (
        <p
          className="mt-3 text-xs text-amber-400"
          data-testid="consignment-unpriced"
        >
          {totals.unpriced} consignment slab
          {totals.unpriced === 1 ? " has" : "s have"} no cost per sq ft —{" "}
          {totals.unpriced === 1 ? "its" : "their"} value is not counted above.
        </p>
      )}

      <div className="mt-4 space-y-2" data-testid="consignment-rows">
        {rows.length === 0 && (
          <p className="rounded-lg border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500">
            No consignment stock on hand. Tick "Consignment" on a slab or
            purchase order to track it here.
          </p>
        )}
        {rows.map((r) => (
          <div
            key={r.supplier}
            className="rounded-lg border border-zinc-800 bg-zinc-900"
            data-testid={`consignment-row-${r.supplier}`}
          >
            <button
              data-testid={`consignment-toggle-${r.supplier}`}
              onClick={() => setOpen(open === r.supplier ? null : r.supplier)}
              className="flex w-full flex-wrap items-center gap-4 p-4 text-left"
            >
              <CaretRight
                size={14}
                weight="bold"
                className={`shrink-0 text-zinc-500 transition-transform duration-150 ${
                  open === r.supplier ? "rotate-90" : ""
                }`}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">
                  {r.supplier}
                </span>
                <span className="mt-0.5 block text-xs text-zinc-500">
                  {r.in_stock} in stock · {r.reserved} reserved · {r.consumed}{" "}
                  used
                  {r.owed_count > 0 ? ` · ${r.owed_count} awaiting payout` : ""}
                </span>
              </span>
              <span className="font-mono-data text-sm text-zinc-300">
                {r.slab_count} slab{r.slab_count === 1 ? "" : "s"}
              </span>
              <span className="font-mono-data text-sm text-emerald-400">
                {r.sqft} ft²
              </span>
              <span className="font-mono-data text-sm font-bold text-yellow-400">
                {money(r.value)}
              </span>
              {r.owed_value > 0 && (
                <span
                  data-testid={`consignment-owed-${r.supplier}`}
                  className="rounded-full border border-red-500/25 bg-red-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-red-400"
                >
                  {money(r.owed_value)} due
                </span>
              )}
            </button>
            {open === r.supplier && (
              <div className="grid gap-2 border-t border-zinc-800 p-4 sm:grid-cols-2">
                {r.slabs.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`consignment-slab-${s.id}`}
                    className="rounded-md border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Link
                        to={`/inventory/slabs/${s.id}`}
                        className="min-w-0 hover:opacity-80"
                      >
                        <span className="font-mono-data block text-xs text-yellow-500">
                          {s.code}
                        </span>
                        <span className="block truncate text-sm font-semibold">
                          {s.slab_color}
                        </span>
                        <span className="block text-xs text-zinc-500">
                          {s.length_in}" x {s.width_in}" · {s.status}
                          {s.location ? ` · rack ${s.location}` : ""}
                        </span>
                      </Link>
                      <span className="font-mono-data shrink-0 text-xs text-zinc-400">
                        {s.sqft} ft² · {money(s.value)}
                      </span>
                    </div>
                    {s.status === "Consumed" && (
                      <div className="mt-3 flex items-center justify-between gap-3 border-t border-zinc-800 pt-3">
                        <span
                          className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest ${
                            s.payout_paid
                              ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
                              : "border-red-500/25 bg-red-500/10 text-red-400"
                          }`}
                        >
                          {s.payout_paid
                            ? `Paid ${(s.payout_paid_at || "").slice(0, 10)}`
                            : "Payout due"}
                        </span>
                        <button
                          data-testid={`payout-toggle-${s.id}`}
                          onClick={() => setPaid(s, !s.payout_paid)}
                          className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
                        >
                          {s.payout_paid ? "Undo" : "Mark Paid"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
